// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module useRealtimeStats
 * @package @geenius/analytics/react-css
 * @description Polls realtime analytics data for the React CSS variant. The
 * hook manages loading state and interval cleanup for dashboard widgets that
 * need continuously refreshed traffic summaries.
 */

import type { RealtimeStats } from "@geenius/analytics";
import { DEFAULT_REALTIME_INTERVAL } from "@geenius/analytics";
import { useEffect, useRef, useState } from "react";

/**
 * Realtime polling state returned by {@link useRealtimeStats}.
 *
 * @property stats The latest successfully fetched realtime statistics.
 * @property isLoading Indicates whether the first fetch is still in flight.
 * @property error The latest polling error as an Error instance, cleared after the next successful fetch.
 */
export interface UseRealtimeStatsReturn {
  stats: RealtimeStats | null;
  isLoading: boolean;
  error: Error | null;
}

function normalizeRealtimeStatsError(err: unknown): Error {
  if (err instanceof Error) {
    return err;
  }

  return new Error(
    typeof err === "string" ? err : "Realtime stats polling failed",
  );
}

/**
 * Polls realtime analytics statistics on a fixed interval.
 *
 * @param fetcher Async function that returns the current realtime statistics snapshot.
 * @param interval Poll interval in milliseconds. Defaults to the shared realtime interval.
 * @returns The latest realtime stats, loading state, and normalized polling error.
 *
 * @example
 * ```tsx
 * const { stats, isLoading } = useRealtimeStats(fetchRealtimeStats, 30_000)
 * ```
 */
export function useRealtimeStats(
  fetcher: () => Promise<RealtimeStats>,
  interval = DEFAULT_REALTIME_INTERVAL,
): UseRealtimeStatsReturn {
  const [stats, setStats] = useState<RealtimeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const fetcherRef = useRef(fetcher);
  const pollInterval = Number.isFinite(interval)
    ? Math.max(1000, interval)
    : DEFAULT_REALTIME_INTERVAL;

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    let active = true;

    const poll = async (): Promise<void> => {
      try {
        const nextStats = await fetcherRef.current();
        if (active) {
          setStats(nextStats);
          setError(null);
        }
      } catch (err) {
        if (active) {
          setError(normalizeRealtimeStatsError(err));
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    void poll();
    const id = setInterval(() => {
      void poll();
    }, pollInterval);

    return () => {
      active = false;
      clearInterval(id);
    };
  }, [pollInterval]);

  return { stats, isLoading, error };
}
